import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTheme, type Breakpoint } from "@mui/material";

export interface ContentSize {
    breakpoint: Breakpoint;
    isOnMediumScreen: boolean;
}

type BreakpointValues = { xs: number; sm: number; md: number; lg: number; xl: number };

const ContentSizeContext = createContext<ContentSize | null>(null);

function breakpointForWidth(width: number, { sm, md, lg, xl }: BreakpointValues): Breakpoint {
    return width < sm ? "xs" : width < md ? "sm" : width < lg ? "md" : width < xl ? "lg" : "xl";
}

function computeContentSize(breakpoint: Breakpoint): ContentSize {
    return {
        breakpoint,
        isOnMediumScreen: breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md",
    };
}

interface IContentSizeProviderProps {
    contentRef: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}

export function ContentSizeProvider({ contentRef, children }: IContentSizeProviderProps) {
    const theme = useTheme();
    const breakpointValues = theme.breakpoints.values;
    // The breakpoint is what goes into state, not the observed width. The content area is a flex item
    // beside the rail, so its width changes on every frame of a rail drag; keeping the width in state
    // re-rendered this provider and every consumer per frame (traced at ~12 ms of React per frame).
    // Setting an unchanged breakpoint is a bail-out, so a drag that stays within one breakpoint costs
    // no render at all.
    const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => breakpointForWidth(typeof window !== "undefined" ? window.innerWidth : 0, breakpointValues));

    useEffect(() => {
        const element = contentRef.current;
        if (!element) return;
        setBreakpoint(breakpointForWidth(element.clientWidth, breakpointValues));
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setBreakpoint(breakpointForWidth(entry.contentRect.width, breakpointValues));
            }
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [contentRef, breakpointValues]);

    // useMemo strictly for performance: a fresh object here re-renders every useContentSize() consumer
    // whenever Content renders, even though nothing they read has changed.
    const value = useMemo(() => computeContentSize(breakpoint), [breakpoint]);
    return <ContentSizeContext.Provider value={value}>{children}</ContentSizeContext.Provider>;
}

function useWindowWidth(enabled: boolean): number {
    const [width, setWidth] = useState<number>(() => (typeof window !== "undefined" ? window.innerWidth : 0));
    useEffect(() => {
        if (!enabled) return;
        function handleResize() {
            setWidth(window.innerWidth);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [enabled]);
    return width;
}

export function useContentSize(): ContentSize {
    const fromContext = useContext(ContentSizeContext);
    const theme = useTheme();
    // The window-resize listener is gated on `!fromContext` so callers inside the provider tree
    // don't re-render every time the window dispatches a resize event (including synthetic ones
    // we fire ourselves, e.g. the kanban auto-scroll's library-recalc nudge). Hook order is kept
    // stable: useContext / useTheme / useWindowWidth always run; only the effect inside
    // useWindowWidth short-circuits.
    const windowWidth = useWindowWidth(fromContext == null);
    return fromContext ?? computeContentSize(breakpointForWidth(windowWidth, theme.breakpoints.values));
}

// Picks a Grid `size` value based on the *content area* breakpoint instead of the viewport.
// MUI Grid's responsive `size={{ xs, md, xl }}` prop keys off viewport media queries — when the
// content area shrinks because the side menu or AI chat is open, those props don't react.
// This helper takes the same shorthand object and resolves it down to a single number using
// the breakpoint reported by `useContentSize()`.
export function contentResponsiveSize(breakpoint: Breakpoint, sizes: Partial<Record<Breakpoint, number>>): number {
    const order: Breakpoint[] = ["xl", "lg", "md", "sm", "xs"];
    const startIndex = order.indexOf(breakpoint);
    for (let i = startIndex; i < order.length; i++) {
        const value = sizes[order[i]];
        if (value != null) return value;
    }
    return 12;
}
